import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'

// Evita que un error de render deje TODA la app en blanco: muestra el error
// de forma legible y permite recargar, en vez de una pantalla vacía.
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(error, info) { try { console.error('App error:', error, info) } catch {} }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', maxWidth: 720, margin: '40px auto' }}>
          <h2 style={{ color: '#dc2626' }}>Algo salió mal en esta pantalla 😕</h2>
          <p style={{ color: '#374151' }}>Recarga la página. Si sigue pasando, este es el detalle técnico:</p>
          <pre style={{ background: '#f3f4f6', padding: 12, borderRadius: 8, overflow: 'auto', fontSize: 12, color: '#991b1b' }}>
            {String(this.state.error?.message || this.state.error)}
            {'\n\n'}
            {String(this.state.error?.stack || '').split('\n').slice(0, 6).join('\n')}
          </pre>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: '#128C7E', color: 'white', fontWeight: 600, cursor: 'pointer' }}>
            Recargar
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './context/AuthContext'

// Landing
import Landing from './pages/Landing'

// Auth pages
import Login from './pages/auth/Login'
import LoginSuperAdmin from './pages/auth/LoginSuperAdmin'

// App pages
import AppLayout from './components/layout/AppLayout'
import Inbox from './pages/app/Inbox'
import Pedidos from './pages/app/Pedidos'
import Proyectos from './pages/app/Proyectos'
import Pipeline from './pages/app/Pipeline'
import Contactos from './pages/app/Contactos'
import Sesiones from './pages/app/Sesiones'
import ConfigBot from './pages/app/ConfigBot'
import Campanas from './pages/app/Campanas'
import Plantillas from './pages/app/Plantillas'
import Respuestas from './pages/app/Respuestas'
import KnowledgeBase from './pages/app/KnowledgeBase'
import Reportes from './pages/app/Reportes'
import Equipo from './pages/app/Equipo'
import Configuracion from './pages/app/Configuracion'
import Integraciones from './pages/app/Integraciones'
import MetricasBot from './pages/app/MetricasBot'
import Negocios from './pages/app/Negocios'
import FlujosEditor from './pages/app/FlujosEditor'

// Super Admin pages
import AdminLayout from './components/layout/AdminLayout'
import AdminDashboard from './pages/admin/Dashboard'
import AdminEmpresas from './pages/admin/Empresas'
import AdminPlanes from './pages/admin/Planes'
import AdminVPS from './pages/admin/VPS'

import './index.css'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'

function ProtectedRoute({ children, soloRol }) {
  const { usuario, cargando } = useAuth()
  if (cargando) return <Loading />
  if (!usuario) return <Navigate to="/login" replace />
  if (soloRol && !soloRol.includes(usuario.rol)) return <Navigate to="/inbox" replace />
  return children
}

function AdminRoute({ children }) {
  const { superAdmin, cargando } = useAuth()
  if (cargando) return <Loading />
  if (!superAdmin) return <Navigate to="/admin/login" replace />
  return children
}

function Loading() {
  return <div className="loading-fullscreen"><div className="spinner" /></div>
}

function HomeRoute() {
  // La APK debe abrir siempre en el formulario de acceso; el sitio web
  // conserva su landing comercial como página de entrada.
  return Capacitor.isNativePlatform()
    ? <Navigate to="/login" replace />
    : <Landing />
}

function Root() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{
          style: { fontSize: 13.5, borderRadius: 8, fontFamily: 'Inter' },
          success: { iconTheme: { primary: '#10b981', secondary: 'white' } },
        }} />
        <Routes>
          {/* Landing */}
          <Route path="/landing" element={<Landing />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<LoginSuperAdmin />} />

          {/* Super Admin */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="empresas" element={<AdminEmpresas />} />
            <Route path="planes" element={<AdminPlanes />} />
            <Route path="vps" element={<AdminVPS />} />
          </Route>

          {/* App principal */}
          <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/inbox" replace />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="contactos" element={<Contactos />} />
            <Route path="pedidos" element={<Pedidos />} />
            <Route path="proyectos" element={<Proyectos />} />
            <Route path="sesiones" element={<ProtectedRoute soloRol={['admin']}><Sesiones /></ProtectedRoute>} />
            <Route path="config-bot/:sesionId" element={<ProtectedRoute soloRol={['admin', 'supervisor']}><ConfigBot /></ProtectedRoute>} />
            <Route path="campanas" element={<ProtectedRoute soloRol={['admin', 'supervisor']}><Campanas /></ProtectedRoute>} />
            <Route path="plantillas" element={<Plantillas />} />
            <Route path="respuestas" element={<Respuestas />} />
            <Route path="kb" element={<KnowledgeBase />} />
            <Route path="reportes" element={<ProtectedRoute soloRol={['admin', 'supervisor']}><Reportes /></ProtectedRoute>} />
            <Route path="equipo" element={<ProtectedRoute soloRol={['admin']}><Equipo /></ProtectedRoute>} />
            <Route path="config" element={<ProtectedRoute soloRol={['admin']}><Configuracion /></ProtectedRoute>} />
            <Route path="integraciones" element={<ProtectedRoute soloRol={['admin']}><Integraciones /></ProtectedRoute>} />
            <Route path="metricas-bot" element={<ProtectedRoute soloRol={['admin','supervisor']}><MetricasBot /></ProtectedRoute>} />
            <Route path="negocios" element={<ProtectedRoute soloRol={['admin']}><Negocios /></ProtectedRoute>} />
            <Route path="flujos/:id" element={<ProtectedRoute soloRol={['admin','supervisor']}><FlujosEditor /></ProtectedRoute>} />
          </Route>

          <Route path="/" element={<HomeRoute />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <ErrorBoundary><Root /></ErrorBoundary>
)

serviceWorkerRegistration.register()
